/**
 * Permission vocabulary — the one owner of every school and platform
 * permission key, read by the backend access module and the web.
 *
 * The backend catalog (apps/backend/src/services/authorization/capabilities/catalog.ts)
 * types its entries from this enum, so a key added here without a catalog
 * description fails type-check.
 */

import { z } from 'zod'

/**
 * Every school permission key. A school membership holds the union of its
 * school roles' keys; a super-admin holds all of them at every school.
 */
export const CapabilityKeySchema = z.enum([
  // Action — content & lifecycle
  'manage_pages',
  'manage_news',
  'manage_announcements',
  'manage_events',
  'manage_ticketed_events',
  'export_ticketed_event_reservations',

  // Action — communication
  'send_sms',
  'send_broadcast',
  'publish_home_messages',

  // Action — media
  'manage_photos',
  'manage_media_collections',
  'manage_media_moderation',

  // Action — directory & users
  'manage_users',
  'manage_school_users',
  'manage_groups',
  'manage_sections',
  'manage_section_members',

  // Action — high privilege
  'manage_school_settings',
  'manage_roles',
  'impersonate_users',

  // Visibility
  'view_school',
  'view_sections',
  'view_students',
  'view_all_directory',
  'view_all_school_chats',
  'view_audit_log',
  'view_ticketed_event_reservations',
  'view_school_users_validation',
  'view_school_admin_dashboard'
])

export type CapabilityKey = z.infer<typeof CapabilityKeySchema>

/**
 * Every platform permission key. Platform roles come from the Clerk session;
 * today the only one is Super Admin (the `isSuperAdmin` claim), which holds
 * `operate_platform`. Staff tiers later split this key.
 */
export const PlatformPermissionSchema = z.enum(['operate_platform'])

export type PlatformPermission = z.infer<typeof PlatformPermissionSchema>

export const CapabilityAxisSchema = z.enum(['action', 'visibility'])
export type CapabilityAxis = z.infer<typeof CapabilityAxisSchema>

export const CapabilityMetadataSchema = z.object({
  key: CapabilityKeySchema,
  axis: CapabilityAxisSchema,
  description: z.string()
})
export type CapabilityMetadata = z.infer<typeof CapabilityMetadataSchema>
